/**
 * Fill Information Component
 * 
 * Comprehensive onboarding forms for Boss (Company Owner) and Dispatcher roles
 * Based on E-Dispatch Registration Form Specification
 */

import React, { useState, useEffect } from 'react'
import { Select } from 'antd'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { authAPI } from '../services/api'
import { handleTokenFromUrl } from '../utils/auth'

type CompanyType = 'CORP' | 'LLC' | 'SOLE_PROP' | 'PARTNERSHIP' | 'OTHER'

interface BossFormData {
  // Company Information
  companyName: string
  businessRegistrationNumber: string
  companyType: CompanyType
  yearFounded: string

  // Contact Information
  primaryContactName: string
  phone: string
  email: string
  street1: string
  street2: string
  city: string
  state: string
  postalCode: string
  country: string
  website: string

  // Operations
  fleetSize: string
  serviceArea: string
  mcDot: string
  scacCode: string
  insuranceProvider: string
  policyNumber: string

  // Account
  inviteCode: string
  firstName: string
  lastName: string
  workEmail: string
  password: string
  confirmPassword: string
}

interface DispatcherFormData {
  // Company Join
  inviteCode: string
  
  // Profile
  firstName: string
  lastName: string
  workEmail: string
  phone: string
  password: string
  confirmPassword: string
}

// Helper function to decode JWT token
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const FillInformation = () => {
  const [searchParams] = useSearchParams()
  const [userRole, setUserRole] = useState<'boss' | 'dispatcher'>('boss')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [isGoogleOAuth, setIsGoogleOAuth] = useState(false)
  const navigate = useNavigate()

  // Boss form state
  const [bossForm, setBossForm] = useState<BossFormData>({
    companyName: '',
    businessRegistrationNumber: '',
    companyType: 'CORP',
    yearFounded: '',
    primaryContactName: '',
    phone: '',
    email: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
    website: '',
    fleetSize: '',
    serviceArea: '',
    mcDot: '',
    scacCode: '',
    insuranceProvider: '',
    policyNumber: '',
    inviteCode: '',
    firstName: '',
    lastName: '',
    workEmail: '',
    password: '',
    confirmPassword: ''
  })

  // Dispatcher form state
  const [dispatcherForm, setDispatcherForm] = useState<DispatcherFormData>({
    inviteCode: '',
    firstName: '',
    lastName: '',
    workEmail: '',
    phone: '',
    password: '',
    confirmPassword: ''
  })

  useEffect(() => {
    const role = searchParams.get('role')
    const token = searchParams.get('token')
    const code = searchParams.get('code')
    const email = searchParams.get('email')

    if (role === 'boss' || role === 'dispatcher') {
      setUserRole(role)
    } else if (role === 'business') {
      setUserRole('boss')
    } else if (role === 'employee') {
      setUserRole('dispatcher')
    } else {
      // If no role specified, default to boss for email verification flow
      setUserRole('boss')
    }

    // Handle Google OAuth code exchange (backend redirects with ?code=XXX&role=business/employee)
    if (code && !email && (role === 'business' || role === 'employee')) {
      setIsGoogleOAuth(true)
      // Exchange one-time code for JWT token via handleTokenFromUrl
      handleTokenFromUrl().then((accessToken) => {
        if (accessToken) {
          // Decode JWT to get user email
          const decodedToken = decodeJWT(accessToken)
          if (decodedToken && decodedToken.email) {
            setBossForm(prev => ({ ...prev, workEmail: decodedToken.email }))
            setDispatcherForm(prev => ({ ...prev, workEmail: decodedToken.email }))
          }
        }
      }).catch((err) => {
        console.error('Failed to exchange OAuth code:', err)
        setError('Google sign-in failed. Please try again.')
      })
      return // Don't process token/email params for OAuth flow
    }

    // Handle email verification flow (has token param)
    if (token) {
      // Legacy Google OAuth: token in URL without email param
      const isLegacyGoogleAuth = !email && (role === 'business' || role === 'employee')
      setIsGoogleOAuth(isLegacyGoogleAuth)

      if (isLegacyGoogleAuth) {
        // Google OAuth - store JWT token directly and extract email from it
        localStorage.setItem('token', token)

        // Decode JWT to get user email
        const decodedToken = decodeJWT(token)
        if (decodedToken && decodedToken.email) {
          setBossForm(prev => ({ ...prev, workEmail: decodedToken.email }))
          setDispatcherForm(prev => ({ ...prev, workEmail: decodedToken.email }))
        }
      } else {
        // Email verification - store verification token
        localStorage.setItem('verification_token', token)
      }
    }

    if (email) {
      // Pre-fill work email for verified users
      setBossForm(prev => ({ ...prev, workEmail: email }))
      setDispatcherForm(prev => ({ ...prev, workEmail: email }))
    }
  }, [searchParams])

  const handleBossFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setBossForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const handleDispatcherFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setDispatcherForm(prev => ({ ...prev, [name]: value }))
  }

  const validatePassword = (password: string): string[] => {
    const errors = []
    if (password.length < 8) errors.push('At least 8 characters')
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter')
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter')
    if (!/\d/.test(password)) errors.push('One digit')
    return errors
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[+]?[1-9][\d]{0,14}$|^\([0-9]{3}\)\s[0-9]{3}-[0-9]{4}$/
    return phoneRegex.test(phone.replace(/\s/g, ''))
  }

  const validateBossForm = (isGoogleOAuth: boolean = false): boolean => {
    const errors = []

    if (!bossForm.companyName || bossForm.companyName.length < 2) errors.push('Company name is required (2-100 chars)')
    if (!bossForm.businessRegistrationNumber) errors.push('Business registration number is required')
    if (!bossForm.primaryContactName || bossForm.primaryContactName.length < 2) errors.push('Primary contact name is required (2-80 chars)')
    if (!validatePhone(bossForm.phone)) errors.push('Valid phone number is required')
    if (!validateEmail(bossForm.email)) errors.push('Valid email is required')
    if (!bossForm.street1) errors.push('Street address is required')
    if (!bossForm.city) errors.push('City is required')
    if (!bossForm.state) errors.push('State is required')
    if (!bossForm.postalCode) errors.push('Postal code is required')
    if (!bossForm.fleetSize || parseInt(bossForm.fleetSize) < 0 || parseInt(bossForm.fleetSize) > 2000) errors.push('Fleet size must be 0-2000')
    if (!bossForm.serviceArea) errors.push('Service area is required')
    if (!bossForm.mcDot) errors.push('USDOT/MC Number is required')
    if (!bossForm.scacCode) errors.push('SCAC Code is required')
    if (!bossForm.inviteCode || bossForm.inviteCode.length < 8) errors.push('Valid invite code is required (8-32 chars)')
    if (!bossForm.firstName) errors.push('First name is required')
    if (!bossForm.lastName) errors.push('Last name is required')
    // Skip work email validation for Google OAuth users (they can use their Google email)
    if (!isGoogleOAuth && !validateEmail(bossForm.workEmail)) errors.push('Valid work email is required')

    // Skip password validation for Google OAuth users
    if (!isGoogleOAuth) {
      const passwordErrors = validatePassword(bossForm.password)
      if (passwordErrors.length > 0) errors.push(`Password must have: ${passwordErrors.join(', ')}`)
      if (bossForm.password !== bossForm.confirmPassword) errors.push('Passwords do not match')
    }

    if (errors.length > 0) {
      setError(errors.join('. '))
      return false
    }
    return true
  }

  const validateDispatcherForm = (isGoogleOAuth: boolean = false): boolean => {
    const errors = []
    
    if (!dispatcherForm.inviteCode || dispatcherForm.inviteCode.length < 8) errors.push('Valid invite code is required (8-32 chars)')
    if (!dispatcherForm.firstName) errors.push('First name is required')
    if (!dispatcherForm.lastName) errors.push('Last name is required')
    // Skip work email validation for Google OAuth users (they can use their Google email)  
    if (!isGoogleOAuth && !validateEmail(dispatcherForm.workEmail)) errors.push('Valid work email is required')
    
    // Skip password validation for Google OAuth users
    if (!isGoogleOAuth) {
      const passwordErrors = validatePassword(dispatcherForm.password)
      if (passwordErrors.length > 0) errors.push(`Password must have: ${passwordErrors.join(', ')}`)
      if (dispatcherForm.password !== dispatcherForm.confirmPassword) errors.push('Passwords do not match')
    }
    
    if (errors.length > 0) {
      setError(errors.join('. '))
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const email = searchParams.get('email')
      const token = searchParams.get('token')

      // Use component-level isGoogleOAuth state (set in useEffect from code or token params)
      // For email verification flow, both email and token must be present
      if (!isGoogleOAuth && (!email || !token)) {
        setError('Missing email verification information. Please start registration again.')
        setIsLoading(false)
        return
      }

      if (userRole === 'boss') {
        if (!validateBossForm(isGoogleOAuth)) {
          setIsLoading(false)
          return
        }
        
        if (isGoogleOAuth) {
          // Google OAuth boss registration: backend validates the TNT invite
          // code, creates trucking_companies, flips users.role pending→boss,
          // and returns a fresh JWT (old pending-role token is invalidated).
          const response = await authAPI.updateProfile({
            firstName: bossForm.firstName,
            lastName: bossForm.lastName,
            phone: bossForm.phone,
            role: 'boss',
            inviteCode: bossForm.inviteCode,
            companyName: bossForm.companyName,
            registrationNumber: bossForm.businessRegistrationNumber,
            mcDot: bossForm.mcDot,
            scacCode: bossForm.scacCode,
            serviceArea: bossForm.serviceArea,
            companyAddress: bossForm.street1,
            companyCity: bossForm.city,
            companyState: bossForm.state,
            companyZipCode: bossForm.postalCode,
            companyEmail: bossForm.email,
            companyPhone: bossForm.phone,
            primaryContactName: bossForm.primaryContactName,
          })

          // Axios response interceptor strips `success` and returns the inner
          // `data` directly — so we detect success by presence of the freshly-
          // promoted user (role=boss, trucking_company_id set).
          if (response?.user?.trucking_company_id && response?.user?.role === 'boss') {
            if (response.token) localStorage.setItem('token', response.token)
            navigate('/dispatch-console')
          } else {
            setError(response?.message || 'Registration failed. Please try again.')
          }
        } else {
          // Email verification user - complete registration
          const response = await authAPI.completeRegistration({
            email: email,
            username: email,
            password: bossForm.password,
            role: 'boss',
            verificationToken: token,
            firstName: bossForm.firstName,
            lastName: bossForm.lastName,
            phone: bossForm.phone,
            companyName: bossForm.companyName,
            registrationNumber: bossForm.businessRegistrationNumber,
            mcDot: bossForm.mcDot,
            scacCode: bossForm.scacCode,
            companyAddress: bossForm.street1,
            companyCity: bossForm.city,
            companyState: bossForm.state,
            companyZipCode: bossForm.postalCode,
            serviceArea: bossForm.serviceArea,
            inviteCode: bossForm.inviteCode
          })
          
          if (response.token) {
            localStorage.setItem('token', response.token)
            navigate('/dispatch-console')
          } else {
            setError(response.message || 'Registration failed. Please try again.')
          }
        }
      } else {
        if (!validateDispatcherForm(isGoogleOAuth)) {
          setIsLoading(false)
          return
        }
        
        if (isGoogleOAuth) {
          // Google OAuth dispatcher registration: backend validates the DSP
          // invite code, flips users.role pending→dispatcher, attaches the
          // tenant from the code, and returns a fresh JWT.
          const response = await authAPI.updateProfile({
            firstName: dispatcherForm.firstName,
            lastName: dispatcherForm.lastName,
            phone: dispatcherForm.phone,
            role: 'dispatcher',
            inviteCode: dispatcherForm.inviteCode,
          })

          if (response?.user?.trucking_company_id && response?.user?.role === 'dispatcher') {
            if (response.token) localStorage.setItem('token', response.token)
            navigate('/dispatch-console')
          } else {
            setError(response?.message || 'Registration failed. Please try again.')
          }
        } else {
          // Email verification user - complete registration
          const response = await authAPI.completeRegistration({
            email: email,
            username: email,
            password: dispatcherForm.password,
            role: 'dispatcher',
            verificationToken: token,
            firstName: dispatcherForm.firstName,
            lastName: dispatcherForm.lastName,
            phone: dispatcherForm.phone,
            inviteCode: dispatcherForm.inviteCode
          })
          
          if (response.token) {
            localStorage.setItem('token', response.token)
            navigate('/dispatch-console')
          } else {
            setError(response.message || 'Registration failed. Please try again.')
          }
        }
      }
    } catch (err: unknown) {
      const message = (err instanceof Error) ? err.message : 'An error occurred. Please try again.';
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const renderBossForm = () => {
    const totalSteps = 4
    
    const renderStepIndicator = () => (
      <div className="flex items-center justify-center mb-8">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              i + 1 <= currentStep ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {i + 1}
            </div>
            {i < totalSteps - 1 && (
              <div className={`w-8 h-0.5 ${
                i + 1 < currentStep ? 'bg-green-600' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>
    )
    
    const renderStep1 = () => (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
        
        <div>
          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
          <Input
            id="companyName"
            name="companyName"
            autoComplete="organization"
            value={bossForm.companyName}
            onChange={handleBossFormChange}
            placeholder="Enter company name\u2026"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            maxLength={100}
            required
          />
        </div>

        <div>
          <label htmlFor="businessRegistrationNumber" className="block text-sm font-medium text-gray-700 mb-2">Business Registration Number *</label>
          <Input
            id="businessRegistrationNumber"
            name="businessRegistrationNumber"
            value={bossForm.businessRegistrationNumber}
            onChange={handleBossFormChange}
            placeholder="EIN or local registration number\u2026"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            required
          />
        </div>

        <div>
          <label htmlFor="companyType" className="block text-sm font-medium text-gray-700 mb-2">Company Type *</label>
          <Select
            id="companyType"
            value={bossForm.companyType}
            onChange={(v) => setBossForm(prev => ({ ...prev, companyType: v as CompanyType }))}
            style={{ width: '100%', height: 44 }}
          >
            <Select.Option value="CORP">Corporation</Select.Option>
            <Select.Option value="LLC">LLC</Select.Option>
            <Select.Option value="SOLE_PROP">Sole Proprietorship</Select.Option>
            <Select.Option value="PARTNERSHIP">Partnership</Select.Option>
            <Select.Option value="OTHER">Other</Select.Option>
          </Select>
        </div>

        <div>
          <label htmlFor="yearFounded" className="block text-sm font-medium text-gray-700 mb-2">Year Founded</label>
          <Input
            id="yearFounded"
            name="yearFounded"
            type="number"
            value={bossForm.yearFounded}
            onChange={handleBossFormChange}
            placeholder="e.g., 2020"
            min="1900"
            max={new Date().getFullYear()}
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
          />
        </div>
      </div>
    )
    
    const renderStep2 = () => (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
        
        <div>
          <label htmlFor="primaryContactName" className="block text-sm font-medium text-gray-700 mb-2">Primary Contact Name *</label>
          <Input
            id="primaryContactName"
            name="primaryContactName"
            autoComplete="name"
            value={bossForm.primaryContactName}
            onChange={handleBossFormChange}
            placeholder="Enter primary contact name\u2026"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            maxLength={80}
            required
          />
        </div>

        <div>
          <label htmlFor="boss-phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
          <Input
            id="boss-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            value={bossForm.phone}
            onChange={handleBossFormChange}
            placeholder="Enter phone number\u2026"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            required
          />
        </div>

        <div>
          <label htmlFor="boss-email" className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
          <Input
            id="boss-email"
            name="email"
            type="email"
            autoComplete="email"
            spellCheck={false}
            value={bossForm.email}
            onChange={handleBossFormChange}
            placeholder="Enter email address\u2026"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label htmlFor="street1" className="block text-sm font-medium text-gray-700 mb-2">Street Address *</label>
            <Input
              id="street1"
              name="street1"
              autoComplete="address-line1"
              value={bossForm.street1}
              onChange={handleBossFormChange}
              placeholder="Enter street address\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              required
            />
          </div>

          <div>
            <label htmlFor="street2" className="block text-sm font-medium text-gray-700 mb-2">Street Address Line 2</label>
            <Input
              id="street2"
              name="street2"
              autoComplete="address-line2"
              value={bossForm.street2}
              onChange={handleBossFormChange}
              placeholder="Apartment, suite, etc.\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">City *</label>
            <Input
              id="city"
              name="city"
              autoComplete="address-level2"
              value={bossForm.city}
              onChange={handleBossFormChange}
              placeholder="Enter city\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              required
            />
          </div>

          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">State *</label>
            <Input
              id="state"
              name="state"
              autoComplete="address-level1"
              value={bossForm.state}
              onChange={handleBossFormChange}
              placeholder="e.g., CA"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              maxLength={2}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">Postal Code *</label>
            <Input
              id="postalCode"
              name="postalCode"
              autoComplete="postal-code"
              value={bossForm.postalCode}
              onChange={handleBossFormChange}
              placeholder="Enter postal code\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              required
            />
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
            <Input
              id="country"
              name="country"
              autoComplete="country"
              value={bossForm.country}
              onChange={handleBossFormChange}
              placeholder="US"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              maxLength={2}
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">Website</label>
          <Input
            id="website"
            name="website"
            type="url"
            autoComplete="url"
            value={bossForm.website}
            onChange={handleBossFormChange}
            placeholder="https://www.example.com"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
          />
        </div>
      </div>
    )
    
    const renderStep3 = () => (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Operations</h3>
        
        <div>
          <label htmlFor="fleetSize" className="block text-sm font-medium text-gray-700 mb-2">Fleet Size *</label>
          <Input
            id="fleetSize"
            name="fleetSize"
            type="number"
            value={bossForm.fleetSize}
            onChange={handleBossFormChange}
            placeholder="Number of vehicles (0\u20132000)"
            min="0"
            max="2000"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            required
          />
        </div>

        <div>
          <label htmlFor="serviceArea" className="block text-sm font-medium text-gray-700 mb-2">Service Area *</label>
          <Input
            id="serviceArea"
            name="serviceArea"
            value={bossForm.serviceArea}
            onChange={handleBossFormChange}
            placeholder="e.g., Oakland, LA/LB"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            required
          />
        </div>

        <div>
          <label htmlFor="mcDot" className="block text-sm font-medium text-gray-700 mb-2">USDOT/MC Number *</label>
          <Input
            id="mcDot"
            name="mcDot"
            value={bossForm.mcDot}
            onChange={handleBossFormChange}
            placeholder="e.g., MC123456 or USDOT1234567"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            required
          />
        </div>

        <div>
          <label htmlFor="scacCode" className="block text-sm font-medium text-gray-700 mb-2">SCAC Code *</label>
          <Input
            id="scacCode"
            name="scacCode"
            value={bossForm.scacCode}
            onChange={handleBossFormChange}
            placeholder="e.g., ABCD"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            maxLength={4}
            required
          />
        </div>

        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-800">Insurance Information</h4>

          <div>
            <label htmlFor="insuranceProvider" className="block text-sm font-medium text-gray-700 mb-2">Insurance Provider</label>
            <Input
              id="insuranceProvider"
              name="insuranceProvider"
              value={bossForm.insuranceProvider}
              onChange={handleBossFormChange}
              placeholder="Enter insurance provider\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="policyNumber" className="block text-sm font-medium text-gray-700 mb-2">Policy Number</label>
            <Input
              id="policyNumber"
              name="policyNumber"
              value={bossForm.policyNumber}
              onChange={handleBossFormChange}
              placeholder="Enter policy number\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            />
          </div>
        </div>
      </div>
    )
    
    const renderStep4 = () => (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Your Account</h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Company Registration Code</h4>
          <p className="text-sm text-blue-700 mb-3">
            You need a registration invite code to create a new company account.
          </p>
          <label htmlFor="boss-inviteCode" className="block text-sm font-medium text-gray-700 mb-2">Invite Code *</label>
          <Input
            id="boss-inviteCode"
            name="inviteCode"
            value={bossForm.inviteCode}
            onChange={handleBossFormChange}
            placeholder="Enter the invite code..."
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            minLength={8}
            maxLength={32}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            The invite code should be 8-32 characters long
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="boss-firstName" className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
            <Input
              id="boss-firstName"
              name="firstName"
              autoComplete="given-name"
              value={bossForm.firstName}
              onChange={handleBossFormChange}
              placeholder="Enter your first name\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              required
            />
          </div>

          <div>
            <label htmlFor="boss-lastName" className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
            <Input
              id="boss-lastName"
              name="lastName"
              autoComplete="family-name"
              value={bossForm.lastName}
              onChange={handleBossFormChange}
              placeholder="Enter your last name\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="boss-workEmail" className="block text-sm font-medium text-gray-700 mb-2">Work Email *</label>
          <Input
            id="boss-workEmail"
            name="workEmail"
            type="email"
            autoComplete="email"
            spellCheck={false}
            value={bossForm.workEmail}
            onChange={handleBossFormChange}
            placeholder="Enter your work email\u2026"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500 bg-gray-50"
            required
            disabled
          />
          <p className="text-xs text-green-600 mt-1">Email verified</p>
        </div>

        {!isGoogleOAuth && (
          <>
            <div>
              <label htmlFor="boss-password" className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
              <Input
                id="boss-password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={bossForm.password}
                onChange={handleBossFormChange}
                placeholder="Enter a strong password\u2026"
                className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 8 characters with uppercase, lowercase, and digit
              </p>
            </div>

            <div>
              <label htmlFor="boss-confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
              <Input
                id="boss-confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={bossForm.confirmPassword}
                onChange={handleBossFormChange}
                placeholder="Confirm your password\u2026"
                className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
                required
              />
            </div>
          </>
        )}
      </div>
    )
    
    const renderCurrentStep = () => {
      switch (currentStep) {
        case 1: return renderStep1()
        case 2: return renderStep2()
        case 3: return renderStep3()
        case 4: return renderStep4()
        default: return renderStep1()
      }
    }
    
    return (
      <div className="space-y-6">
        {renderStepIndicator()}
        {renderCurrentStep()}
        
        <div className="flex justify-between pt-6">
          {currentStep > 1 && (
            <Button
              type="button"
              onClick={() => setCurrentStep(prev => prev - 1)}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            >
              Previous
            </Button>
          )}
          
          {currentStep < totalSteps ? (
            <Button
              type="button"
              onClick={() => setCurrentStep(prev => prev + 1)}
              className="ml-auto px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
            >
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isLoading}
              className="ml-auto px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Creating Account\u2026' : 'Create Company Account'}
            </Button>
          )}
        </div>
      </div>
    )
  }

  const renderDispatcherForm = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Join Existing Company</h4>
        <p className="text-sm text-blue-700">
          You need an invite code from your company administrator to join.
        </p>
      </div>
      
      <div>
        <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700 mb-2">Company Invite Code *</label>
        <Input
          id="inviteCode"
          name="inviteCode"
          value={dispatcherForm.inviteCode}
          onChange={handleDispatcherFormChange}
          placeholder="Enter the invite code\u2026"
          className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
          minLength={8}
          maxLength={32}
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          The invite code should be 8\u201332 characters long
        </p>
      </div>

      <hr className="border-gray-200" />

      <h4 className="text-md font-medium text-gray-900">Your Profile Information</h4>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="disp-firstName" className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
          <Input
            id="disp-firstName"
            name="firstName"
            autoComplete="given-name"
            value={dispatcherForm.firstName}
            onChange={handleDispatcherFormChange}
            placeholder="Enter your first name\u2026"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            required
          />
        </div>

        <div>
          <label htmlFor="disp-lastName" className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
          <Input
            id="disp-lastName"
            name="lastName"
            autoComplete="family-name"
            value={dispatcherForm.lastName}
            onChange={handleDispatcherFormChange}
            placeholder="Enter your last name\u2026"
            className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
            required
          />
        </div>
      </div>

      <div>
        <label htmlFor="disp-workEmail" className="block text-sm font-medium text-gray-700 mb-2">Work Email *</label>
        <Input
          id="disp-workEmail"
          name="workEmail"
          type="email"
          autoComplete="email"
          spellCheck={false}
          value={dispatcherForm.workEmail}
          onChange={handleDispatcherFormChange}
          placeholder="Enter your work email\u2026"
          className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500 bg-gray-50"
          required
          disabled
        />
        <p className="text-xs text-green-600 mt-1">
          Email verified
        </p>
      </div>

      <div>
        <label htmlFor="disp-phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
        <Input
          id="disp-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          value={dispatcherForm.phone}
          onChange={handleDispatcherFormChange}
          placeholder="Enter your phone number\u2026"
          className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
        />
      </div>

      {!isGoogleOAuth && (
        <>
          <div>
            <label htmlFor="disp-password" className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
            <Input
              id="disp-password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={dispatcherForm.password}
              onChange={handleDispatcherFormChange}
              placeholder="Enter a strong password\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Password must be at least 8 characters with uppercase, lowercase, and digit
            </p>
          </div>

          <div>
            <label htmlFor="disp-confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
            <Input
              id="disp-confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={dispatcherForm.confirmPassword}
              onChange={handleDispatcherFormChange}
              placeholder="Confirm your password\u2026"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-green-500"
              required
            />
          </div>
        </>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Logo */}
      <div className="sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="flex items-center justify-center gap-2.5">
          <img src="/logo.png" alt="EAloom" className="w-9 h-9 rounded-lg" />
          <h1 className="text-2xl font-bold text-gray-900">EAloom</h1>
        </div>
      </div>

      <div className={`mt-8 sm:mx-auto sm:w-full ${userRole === 'boss' ? 'sm:max-w-4xl' : 'sm:max-w-md'}`}>
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 text-center">
              {userRole === 'boss' ? 'Company Registration' : 'Join Your Company'}
            </h2>
            <p className="mt-2 text-sm text-gray-600 text-center">
              {userRole === 'boss' 
                ? 'Create your company account and start managing your dispatch operations' 
                : 'Join your existing company as a dispatcher using an invite code'
              }
            </p>
          </div>

          {/* Role Indicator */}
          <div className="mb-6">
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                onClick={() => setUserRole('boss')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  userRole === 'boss' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Company Owner (Boss)
              </button>
              <button
                type="button"
                onClick={() => setUserRole('dispatcher')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  userRole === 'dispatcher' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Dispatcher (Employee)
              </button>
            </div>
          </div>

          {/* Error message */}
          <div aria-live="polite" aria-atomic="true">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {userRole === 'boss' ? renderBossForm() : renderDispatcherForm()}

            {/* Submit button for dispatcher (boss form has its own submit in steps) */}
            {userRole === 'dispatcher' && (
              <div className="mt-6">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  {isLoading ? 'Joining Company\u2026' : 'Join Company'}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}

export default FillInformation